/**
 * OilSection Component
 * 
 * Container component to display Oil data
 * components in a horizontal Row layout.
 * 
 * This component integrates with the equipment context to pass site, asset, and position
 * props to child components. Individual child components handle their own data fetching
 * to avoid duplicate API calls.
 */

import React, { useCallback } from 'react';
import Heading from '@amzn/meridian/heading';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import DataTable, {
  DataTableBody,
  DataTableHeader,
  DataTableCell,
  DataTableRow,
  DataTableHead,
} from "@amzn/meridian/data-table";
import { useEquipmentContext } from '../../../contexts/equipment-context';
import { useOilData } from '../../../hooks';

export interface OilSectionProps {
}

/**
 * UltrasoundSection container component that uses Meridian Row component
 * with ultrasound UI components as children. This component creates a 50/50
 * column split between trend chart and baseline components.
 * 
 * Integrates with equipment context to get site, asset, and position parameters
 * and passes them to child components. Child components handle their own data 
 * fetching to prevent duplicate API calls.
 */
export const OilSection: React.FC<OilSectionProps> = ({
}) => {
  // Get equipment context state
  const { state } = useEquipmentContext();
  
  // Extract parameters from equipment context only
  const site = state.site || '';
  const asset = state.apmId || '';
  const position = state.position || '';
  const { data, isLoading, error, retry } = useOilData({
    site,
    asset,
    position
  });
  const title = 'Oil Sampling (Particle Count)';

  if (isLoading) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">{title}</Text>
          <Row spacing="200">
            <Loader size="small" type="circular" />
            <Text type="b200">Loading oil sampling data...</Text>
          </Row>
        </Column>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">{title}</Text>
          <Alert type="error">
            <Column spacing="100">
              <Text type="b200">{error}</Text>
              <Button type="secondary" size="small" onClick={retry}>
                Retry
              </Button>
            </Column>
          </Alert>
        </Column>
      </Card>
    );
  }

  if (!data || !data.samples || data.samples.length == 0) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">{title}</Text>
          <Alert type="neutral">
            <Text type="b200">No Oil sampling data available for the selected position and date range</Text>
          </Alert>
        </Column>
      </Card>
    );
  }

  return (
    <Row spacing="400">
        <Column>
            <Heading level={4}>{title}</Heading>
            <DataTable
            showRowDividers={false}
            showStripes={true}
            showBorder={true}
            >
                <DataTableHead>
                    <DataTableRow
                    highlightOnHover={true}
                    >
                    <DataTableHeader>Action Flag</DataTableHeader>
                    <DataTableHeader>Severity</DataTableHeader>
                    <DataTableHeader>Description</DataTableHeader>
                    <DataTableHeader>Date Completed</DataTableHeader>
                    </DataTableRow>
                </DataTableHead>
                <DataTableBody>
                    {data.samples.map((item, idx )=> (
                        <DataTableRow key={idx} highlightOnHover={true}>
                            <DataTableCell>{item.actionFlag}</DataTableCell>
                            <DataTableCell>{item.severity}</DataTableCell>
                            <DataTableCell>{item.comment}</DataTableCell>
                            <DataTableCell>{item.dateCompleted}</DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </Column>
    </Row>    
  );
};